import FooterRedesign from './Footer.redesign';
import { useLang } from '../hooks/useLang';

const copies = {
  de: {
    badge: 'Investor Relations',
    heroTag: 'Institutionelle Private-Credit-Plattform',
    headline: 'Investieren in Stabilität: EU-regulierter Agrarkredit',
    paragraph:
      'Eine Private-Credit-Strategie, ausgelegt auf Kapitalerhalt und planbare, euro-denominierte Renditen.',
    secondaryHeadline: 'Die Asset-Klasse für Kapitalerhalt',
    secondarySubheadline:
      'Stabile Renditen aus dem europäischen Agrarrückgrat, abgesichert durch Regulierung und Technologie.',
    ctaSummary: 'Executive Summary (PDF)',
    ctaDeck: 'Vollständiges Deck anfordern',
    ctaContact: 'Mit dem Team sprechen',
    quickFactsTitle: 'Kernparameter',
    quickFactsSubtitle:
      'Institutionelles Setup mit planbaren Cashflows und klarer Governance.',
    quickFacts: [
      {
        label: 'Währung',
        value: 'EUR',
        detail: 'Euro-denominierte Forderungen mit direktem CAP-Bezug.',
      },
      {
        label: 'Laufzeit',
        value: '30–180 Tage',
        detail: 'Kurzfristige Tranchen mit fortlaufender Tilgung.',
      },
      {
        label: 'Struktur',
        value: 'Bankruptcy-remote SPV',
        detail: 'Segregierte Emissionsstruktur mit unabhängiger Governance.',
      },
      {
        label: 'Region',
        value: 'EU (IT, ES)',
        detail: 'Startmärkte Italien & Spanien, europaweit skalierbar.',
      },
    ],
    valueTitle: 'Investment-Highlights',
    valueSubtitle: 'Zentrale Argumente für institutionelle Investoren.',
    tiles: [
      {
        title: 'Volumen',
        text: '€60 Mrd.+ CAP-Zahlungen mit staatlich gestützten Cash-Zyklen.',
      },
      {
        title: 'Liquiditätsbedarf',
        text: '4–8 Monate Auszahlungslücke schafft kontinuierlichen Brückenbedarf.',
      },
      {
        title: 'Plattform',
        text: 'Datengestützte Analyse, Underwriting und Servicing.',
      },
      {
        title: 'Status',
        text: 'Pilotstruktur vorbereitet; institutionelles Onboarding aktiv.',
      },
    ],
    structureTitle: 'Portfolio-Rahmen',
    structureSubtitle:
      'Transparente Prozesse für Underwriting, Servicing und Reporting.',
    structureItems: [
      {
        title: 'Forderungsbesichert',
        detail: 'Abtretung von CAP-Zahlungsansprüchen und Vertragsforderungen.',
      },
      {
        title: 'Datengestützte Überwachung',
        detail: 'Live-Daten zu Förderstatus, Sicherheiten und Servicing-Meilensteinen.',
      },
      {
        title: 'Risikogesteuerte Allokation',
        detail: 'Standardisierter Kreditprozess mit Investment-Committee-Governance.',
      },
      {
        title: 'Unabhängiges Servicing',
        detail: 'Spezialisierte Partner für Zahlungsabwicklung und Reporting.',
      },
    ],
    diligenceTitle: 'Due Diligence & Compliance',
    diligenceSubtitle:
      'Betrieb im Einklang mit EU-Regulierung und institutionellen Richtlinien.',
    diligenceItems: [
      { title: 'MiFID II', detail: 'Vertriebssetup für professionelle Investoren.' },
      { title: 'DSGVO', detail: 'EU-konformes Datenmanagement und Hosting.' },
      { title: 'AML / KYC', detail: 'Institutionelle Onboarding- und Monitoring-Prozesse.' },
      { title: 'ESMA', detail: 'Reporting-Paket abgestimmt auf Aufsichtsanforderungen.' },
    ],
    contactTitle: 'Direkter Austausch',
    contactSubtitle:
      'Vertrauliche Gespräche für institutionelle und qualifizierte Investoren.',
    contactNote: 'Antwort in der Regel innerhalb von 24 Stunden.',
    mailSubject: 'CENTEO IR Anfrage',
    mailBody: 'Hallo Centeo, ich interessiere mich für weitere IR-Informationen.',
    contactPhoneLabel: 'Direktwahl',
    contactEmailLabel: 'Hauptkontakt',
    platformBlurb:
      'Centeo strukturiert europäische Agrarförderungen als investierbares Private-Credit-Produkt.',
    disclaimerTitle: 'Disclaimer',
    disclaimerParagraphs: [
      'Die Informationen auf dieser Website dienen ausschließlich allgemeinen Informationszwecken und stellen weder Anlageberatung noch ein Angebot oder eine Aufforderung zum Kauf oder Verkauf eines Finanzinstruments dar.',
      'Die Produkte und Dienstleistungen von Centeo richten sich ausschließlich an professionelle und institutionelle Anleger im Sinne der geltenden Gesetze und Vorschriften. Sie sind nicht an Privatkunden oder Personen in Jurisdiktionen gerichtet, in denen eine solche Verbreitung gegen lokale Gesetze oder Vorschriften verstoßen würde.',
      'Vergangene Wertentwicklungen, Backtesting-Ergebnisse oder prognostizierte Renditen sind keine verlässlichen Indikatoren für zukünftige Ergebnisse. Es kann keine Garantie dafür übernommen werden, dass Anlageziele erreicht oder Verluste vermieden werden.',
      'Anlageentscheidungen sollten ausschließlich auf Grundlage einer unabhängigen Due-Diligence-Prüfung und der Beratung durch professionelle Berater getroffen werden. Mit dem Zugriff auf diese Seite bestätigen Sie, dass Sie ein professioneller oder institutioneller Anleger sind und die oben beschriebenen Einschränkungen verstehen und akzeptieren.'
    ]
  },
  en: {
    badge: 'Investor Relations',
    heroTag: 'Institutional private credit platform',
    headline: 'Investing in Stability: EU-Regulated Agricultural Credit',
    paragraph:
      'A private credit strategy designed for capital preservation and predictable, Euro-denominated yields.',
    secondaryHeadline: 'The Capital Preservation Asset Class',
    secondarySubheadline:
      'Generating stable returns from the European agricultural backbone, de-risked by policy and technology.',
    ctaSummary: 'Executive Summary (PDF)',
    ctaDeck: 'Request full deck',
    ctaContact: 'Speak with the team',
    quickFactsTitle: 'Key parameters',
    quickFactsSubtitle:
      'Institutional-grade setup designed for capital preservation and predictable cash cycles.',
    quickFacts: [
      {
        label: 'Currency',
        value: 'EUR',
        detail: 'Euro-denominated exposure linked to EU subsidy flows.',
      },
      {
        label: 'Tenor',
        value: '30–180 days',
        detail: 'Short-duration notes with continuous recycling.',
      },
      {
        label: 'Structure',
        value: 'Bankruptcy-remote SPV',
        detail: 'Segregated issuance vehicle with independent governance.',
      },
      {
        label: 'Geography',
        value: 'EU (IT, ES)',
        detail: 'Initial focus on Italy and Spain, scalable EU-wide.',
      },
    ],
    valueTitle: 'Investment highlights',
    valueSubtitle: 'Core theses underpinning the strategy.',
    tiles: [
      {
        title: 'Scale',
        text: '€60bn+ CAP flows with recurring, government-backed cycles.',
      },
      {
        title: 'Demand',
        text: '4–8 month subsidy delays create consistent liquidity gaps.',
      },
      {
        title: 'Platform',
        text: 'Data-led underwriting, servicing, and monitoring.',
      },
      {
        title: 'Progress',
        text: 'Pilot transactions prepared; institutional onboarding active.',
      },
    ],
    structureTitle: 'Portfolio framework',
    structureSubtitle:
      'Transparent underwriting and servicing built for institutional mandates.',
    structureItems: [
      {
        title: 'Receivables-backed',
        detail: 'Collateralised by assigned CAP subsidy receivables and farmer contracts.',
      },
      {
        title: 'Data-driven monitoring',
        detail: 'Live data pipeline on subsidy status, collateral files, and servicing events.',
      },
      {
        title: 'Risk-managed deployment',
        detail: 'Standardised credit process with investment committee oversight.',
      },
      {
        title: 'Independent servicing',
        detail: 'Specialist partners managing payments, reconciliation, and reporting.',
      },
    ],
    diligenceTitle: 'Diligence & compliance',
    diligenceSubtitle:
      'Operating within EU regulatory standards and institutional policies.',
    diligenceItems: [
      {
        title: 'MiFID II',
        detail: 'Distribution structure aligned with professional-investor requirements.',
      },
      {
        title: 'GDPR',
        detail: 'EU-compliant data governance and hosting.',
      },
      {
        title: 'AML / KYC',
        detail: 'Institutional onboarding and ongoing monitoring procedures.',
      },
      {
        title: 'ESMA',
        detail: 'Reporting package aligned with supervisory expectations.',
      },
    ],
    contactTitle: 'Speak with our team',
    contactSubtitle:
      'Confidential discussions for institutional and qualified investors.',
    contactNote: 'We typically respond within 24 hours.',
    mailSubject: 'CENTEO Institutional IR Inquiry',
    mailBody: 'Hello Centeo, I am interested in further IR information.',
    contactPhoneLabel: 'Direct line',
    contactEmailLabel: 'Primary contact',
    platformBlurb:
      'Centeo structures European agricultural subsidies into an investable private credit product.',
    disclaimerTitle: 'Disclaimer',
    disclaimerParagraphs: [
      'The information on this website is provided for informational purposes only and does not constitute investment advice, an offer, or a solicitation to buy or sell any financial instrument.',
      'Centeo’s products and services are intended exclusively for professional and institutional investors within the meaning of applicable laws and regulations. They are not directed at, or intended for use by, retail investors or any person in any jurisdiction where such distribution would be contrary to local law or regulation.',
      'Past performance, backtesting results, or projected returns are not reliable indicators of future performance. No assurance can be given that investment objectives will be achieved or that losses will be avoided.',
      'Any investment decision should be made solely on the basis of independent due diligence and consultation with professional advisors. By accessing this site, you acknowledge that you are a professional or institutional investor and that you understand and accept the limitations described above.'
    ]
  },
  es: {
    badge: 'Relaciones con Inversores',
    heroTag: 'Plataforma institucional de private credit',
    headline: 'Invertir en estabilidad: crédito agrícola regulado por la UE',
    paragraph:
      'Estrategia de private credit diseñada para preservar capital y obtener rendimientos previsibles denominados en euros.',
    secondaryHeadline: 'La clase de activo para preservar capital',
    secondarySubheadline:
      'Rendimientos estables del tejido agrícola europeo, mitigados por la regulación y la tecnología.',
    ctaSummary: 'Resumen ejecutivo (PDF)',
    ctaDeck: 'Solicitar deck completo',
    ctaContact: 'Hablar con el equipo',
    quickFactsTitle: 'Parámetros clave',
    quickFactsSubtitle:
      'Estructura institucional orientada a la preservación del capital y flujos predecibles.',
    quickFacts: [
      {
        label: 'Moneda',
        value: 'EUR',
        detail: 'Exposición denominada en euros vinculada a flujos de subvenciones CAP.',
      },
      {
        label: 'Plazo',
        value: '30–180 días',
        detail: 'Notas de corta duración con reciclaje continuo.',
      },
      {
        label: 'Estructura',
        value: 'SPV bankruptcy-remote',
        detail: 'Vehículo segregado con gobernanza independiente.',
      },
      {
        label: 'Geografía',
        value: 'UE (IT, ES)',
        detail: 'Enfoque inicial en Italia y España, escalable a toda la UE.',
      },
    ],
    valueTitle: 'Puntos destacados de inversión',
    valueSubtitle: 'Tesis principales que sostienen la estrategia.',
    tiles: [
      {
        title: 'Escala',
        text: 'Más de €60.000 M en flujos CAP con ciclos recurrentes respaldados por gobiernos.',
      },
      {
        title: 'Demanda',
        text: 'Retrasos de 4–8 meses generan brechas de liquidez constantes.',
      },
      {
        title: 'Plataforma',
        text: 'Underwriting, servicing y monitorización guiados por datos.',
      },
      {
        title: 'Progreso',
        text: 'Transacciones piloto preparadas; onboarding institucional activo.',
      },
    ],
    structureTitle: 'Marco de cartera',
    structureSubtitle:
      'Procesos transparentes de underwriting y servicing para mandatos institucionales.',
    structureItems: [
      {
        title: 'Respaldado por cuentas a cobrar',
        detail: 'Garantizado por cesiones de subvenciones CAP y contratos agrícolas.',
      },
      {
        title: 'Monitorización basada en datos',
        detail: 'Canal de datos en vivo sobre estado de subvenciones y hitos de servicing.',
      },
      {
        title: 'Despliegue gestionado por riesgo',
        detail: 'Proceso crediticio estandarizado con comité de inversiones.',
      },
      {
        title: 'Servicing independiente',
        detail: 'Socios especializados para pagos, conciliación e informes.',
      },
    ],
    diligenceTitle: 'Due diligence y cumplimiento',
    diligenceSubtitle:
      'Operamos conforme a los estándares regulatorios de la Unión Europea.',
    diligenceItems: [
      {
        title: 'MiFID II',
        detail: 'Estructura de distribución alineada con inversores profesionales.',
      },
      {
        title: 'RGPD',
        detail: 'Gobernanza y alojamiento de datos conforme a la normativa europea.',
      },
      {
        title: 'AML / KYC',
        detail: 'Procedimientos institucionales de onboarding y seguimiento.',
      },
      {
        title: 'ESMA',
        detail: 'Paquete de reporting alineado con expectativas supervisoras.',
      },
    ],
    contactTitle: 'Contacte con nuestro equipo',
    contactSubtitle:
      'Conversaciones confidenciales para inversores institucionales y cualificados.',
    contactNote: 'Respondemos habitualmente en menos de 24 horas.',
    mailSubject: 'Consulta IR institucional CENTEO',
    mailBody: 'Hola Centeo, me interesa obtener más información para inversores.',
    contactPhoneLabel: 'Línea directa',
    contactEmailLabel: 'Contacto principal',
    platformBlurb:
      'Centeo estructura las subvenciones agrícolas europeas como un activo de private credit.',
    disclaimerTitle: 'Aviso legal',
    disclaimerParagraphs: [
      'La información proporcionada en este sitio web tiene carácter meramente informativo y no constituye asesoramiento de inversión, oferta ni solicitud para comprar o vender ningún instrumento financiero.',
      'Los productos y servicios de Centeo están dirigidos exclusivamente a inversores profesionales e institucionales conforme a las leyes y regulaciones aplicables. No están destinados a inversores minoristas ni a personas en jurisdicciones donde su distribución contravenga la normativa local.',
      'El rendimiento pasado, los resultados de backtesting o las proyecciones de retorno no son indicadores fiables del rendimiento futuro. No se puede asegurar que se alcancen los objetivos de inversión ni que se eviten pérdidas.',
      'Cualquier decisión de inversión debe basarse únicamente en una due diligence independiente y en la consulta con asesores profesionales. Al acceder a este sitio, usted reconoce que es un inversor profesional o institucional y que comprende y acepta las limitaciones descritas.'
    ]
  },
  it: {
    badge: 'Investor Relations',
    heroTag: 'Piattaforma istituzionale di private credit',
    headline: 'Investire nella stabilità: credito agricolo regolamentato dall’UE',
    paragraph:
      'Strategia di private credit pensata per la preservazione del capitale e rendimenti prevedibili denominati in euro.',
    secondaryHeadline: 'La asset class della preservazione del capitale',
    secondarySubheadline:
      'Rendimenti stabili dal tessuto agricolo europeo, mitigati da politiche e tecnologia.',
    ctaSummary: 'Executive Summary (PDF)',
    ctaDeck: 'Richiedi deck completo',
    ctaContact: 'Parla con il team',
    quickFactsTitle: 'Parametri chiave',
    quickFactsSubtitle:
      'Impostazione di livello istituzionale con focus su capitale e cash flow prevedibile.',
    quickFacts: [
      {
        label: 'Valuta',
        value: 'EUR',
        detail: 'Esposizione denominata in euro legata ai flussi CAP.',
      },
      {
        label: 'Durata',
        value: '30–180 giorni',
        detail: 'Note a breve termine con riciclo continuo.',
      },
      {
        label: 'Struttura',
        value: 'SPV bankruptcy-remote',
        detail: 'Veicolo segregato con governance indipendente.',
      },
      {
        label: 'Geografia',
        value: 'UE (IT, ES)',
        detail: 'Focus iniziale su Italia e Spagna, scalabile in tutta l’UE.',
      },
    ],
    valueTitle: 'Punti di investimento',
    valueSubtitle: 'Le tesi principali che sostengono la strategia.',
    tiles: [
      {
        title: 'Scala',
        text: 'Oltre €60 mld di flussi CAP con cicli ricorrenti garantiti dallo Stato.',
      },
      {
        title: 'Domanda',
        text: 'Ritardi di 4–8 mesi generano un fabbisogno costante di liquidità.',
      },
      {
        title: 'Piattaforma',
        text: 'Underwriting, servicing e monitoraggio guidati dai dati.',
      },
      {
        title: 'Progresso',
        text: 'Transazioni pilota pronte; onboarding istituzionale in corso.',
      },
    ],
    structureTitle: 'Framework di portafoglio',
    structureSubtitle:
      'Processi trasparenti di underwriting e servicing per mandati istituzionali.',
    structureItems: [
      {
        title: 'Garanzia su crediti',
        detail: 'Cessioni di sovvenzioni CAP e contratti agricoli come collaterale.',
      },
      {
        title: 'Monitoraggio data-driven',
        detail: 'Pipeline dati in tempo reale su stato delle sovvenzioni e servicing.',
      },
      {
        title: 'Allocazione gestita dal rischio',
        detail: 'Processo creditizio standardizzato con comitato investimenti.',
      },
      {
        title: 'Servicing indipendente',
        detail: 'Partner specializzati per pagamenti, riconciliazioni e reporting.',
      },
    ],
    diligenceTitle: 'Due diligence e compliance',
    diligenceSubtitle:
      'Operiamo secondo gli standard normativi dell’UE.',
    diligenceItems: [
      {
        title: 'MiFID II',
        detail: 'Struttura di distribuzione conforme per investitori professionali.',
      },
      {
        title: 'GDPR',
        detail: 'Governance dei dati e hosting pienamente conformi.',
      },
      {
        title: 'AML / KYC',
        detail: 'Procedure istituzionali di onboarding e monitoraggio continuativo.',
      },
      {
        title: 'ESMA',
        detail: 'Reporting allineato alle aspettative di vigilanza.',
      },
    ],
    contactTitle: 'Parla con il nostro team',
    contactSubtitle:
      'Confronto confidenziale per investitori istituzionali e qualificati.',
    contactNote: 'Rispondiamo di norma entro 24 ore.',
    mailSubject: 'Richiesta IR istituzionale CENTEO',
    mailBody: 'Ciao Centeo, sono interessato a ulteriori informazioni IR.',
    contactPhoneLabel: 'Linea diretta',
    contactEmailLabel: 'Contatto principale',
    platformBlurb:
      'Centeo struttura le sovvenzioni agricole europee in un prodotto di private credit investibile.',
    disclaimerTitle: 'Disclaimer',
    disclaimerParagraphs: [
      'Le informazioni contenute in questo sito sono fornite esclusivamente a scopo informativo e non costituiscono consulenza in materia di investimenti, né un\'offerta o sollecitazione all\'acquisto o alla vendita di strumenti finanziari.',
      'I prodotti e i servizi di Centeo sono destinati esclusivamente a investitori professionali e istituzionali ai sensi delle leggi e normative applicabili. Non sono rivolti a investitori retail né a soggetti in giurisdizioni in cui tale distribuzione sarebbe contraria alla normativa locale.',
      'I rendimenti passati, i risultati di backtesting o le proiezioni di rendimento non sono indicatori affidabili della performance futura. Non vi è alcuna garanzia che gli obiettivi di investimento saranno raggiunti o che si eviteranno perdite.',
      'Qualsiasi decisione di investimento deve basarsi esclusivamente su una due diligence indipendente e sulla consulenza di professionisti qualificati. Accedendo a questo sito, confermate di essere un investitore professionale o istituzionale e di comprendere e accettare le limitazioni sopra descritte.'
    ]
  },
  pt: {
    badge: 'Relações com Investidores',
    heroTag: 'Plataforma institucional de private credit',
    headline: 'Investir em estabilidade: crédito agrícola regulado pela UE',
    paragraph:
      'Estratégia de private credit concebida para preservação de capital e rendimentos previsíveis denominados em euros.',
    secondaryHeadline: 'A classe de ativos da preservação de capital',
    secondarySubheadline:
      'Rendimentos estáveis a partir da espinha dorsal agrícola europeia, mitigados por política e tecnologia.',
    ctaSummary: 'Resumo executivo (PDF)',
    ctaDeck: 'Solicitar deck completo',
    ctaContact: 'Falar com a equipa',
    quickFactsTitle: 'Parâmetros chave',
    quickFactsSubtitle:
      'Estrutura de nível institucional orientada para capital preservado e fluxos previsíveis.',
    quickFacts: [
      {
        label: 'Moeda',
        value: 'EUR',
        detail: 'Exposição denominada em euros ligada a fluxos CAP.',
      },
      {
        label: 'Prazo',
        value: '30–180 dias',
        detail: 'Notas de curta duração com reciclagem contínua.',
      },
      {
        label: 'Estrutura',
        value: 'SPV bankruptcy-remote',
        detail: 'Veículo segregado com governação independente.',
      },
      {
        label: 'Geografia',
        value: 'UE (IT, ES)',
        detail: 'Foco inicial em Itália e Espanha, escalável em toda a UE.',
      },
    ],
    valueTitle: 'Destaques de investimento',
    valueSubtitle: 'Teses centrais que sustentam a estratégia.',
    tiles: [
      {
        title: 'Escala',
        text: 'Mais de €60 mil M em fluxos CAP com ciclos recorrentes garantidos pelo Estado.',
      },
      {
        title: 'Procura',
        text: 'Atrasos de 4–8 meses geram necessidades permanentes de liquidez.',
      },
      {
        title: 'Plataforma',
        text: 'Underwriting, servicing e monitorização orientados por dados.',
      },
      {
        title: 'Progresso',
        text: 'Transações piloto preparadas; onboarding institucional ativo.',
      },
    ],
    structureTitle: 'Estrutura de portefólio',
    structureSubtitle:
      'Processos transparentes de underwriting e servicing para mandatos institucionais.',
    structureItems: [
      {
        title: 'Garantia em recebíveis',
        detail: 'Cessão de subsídios CAP e contratos agrícolas como colateral.',
      },
      {
        title: 'Monitorização baseada em dados',
        detail: 'Pipeline em tempo real sobre estado das subvenções e eventos de servicing.',
      },
      {
        title: 'Alocação controlada por risco',
        detail: 'Processo de crédito padronizado com comité de investimento.',
      },
      {
        title: 'Servicing independente',
        detail: 'Parceiros especializados para pagamentos, reconciliação e reporting.',
      },
    ],
    diligenceTitle: 'Due diligence e compliance',
    diligenceSubtitle:
      'Operação alinhada com os padrões regulatórios da UE.',
    diligenceItems: [
      {
        title: 'MiFID II',
        detail: 'Estrutura de distribuição adequada a investidores profissionais.',
      },
      {
        title: 'RGPD',
        detail: 'Governança e alojamento de dados em conformidade europeia.',
      },
      {
        title: 'AML / KYC',
        detail: 'Processos institucionais de onboarding e monitorização contínua.',
      },
      {
        title: 'ESMA',
        detail: 'Reporting alinhado com as expectativas de supervisão.',
      },
    ],
    contactTitle: 'Fale com a nossa equipa',
    contactSubtitle:
      'Conversas confidenciais para investidores institucionais e qualificados.',
    contactNote: 'Respondemos normalmente em menos de 24 horas.',
    mailSubject: 'Pedido IR institucional CENTEO',
    mailBody: 'Olá Centeo, estou interessado em mais informações para investidores.',
    contactPhoneLabel: 'Linha direta',
    contactEmailLabel: 'Contacto principal',
    platformBlurb:
      'A Centeo estrutura subsídios agrícolas europeus como ativo investível de private credit.',
    disclaimerTitle: 'Aviso legal',
    disclaimerParagraphs: [
      'As informações disponibilizadas neste site têm caráter meramente informativo e não constituem aconselhamento de investimento, nem uma oferta ou solicitação para comprar ou vender qualquer instrumento financeiro.',
      'Os produtos e serviços da Centeo destinam-se exclusivamente a investidores profissionais e institucionais nos termos das leis e regulamentos aplicáveis. Não se destinam a investidores de retalho nem a qualquer pessoa em jurisdições onde tal distribuição seja contrária à legislação local.',
      'O desempenho passado, resultados de backtesting ou projeções de retorno não são indicadores fiáveis do desempenho futuro. Não há garantia de que os objetivos de investimento serão alcançados ou de que perdas serão evitadas.',
      'Qualquer decisão de investimento deve basear-se exclusivamente numa due diligence independente e na consulta a consultores profissionais. Ao aceder a este site, declara ser um investidor profissional ou institucional e que compreende e aceita as limitações acima descritas.'
    ]
  },
};

const InvestorLanding = () => {
  const { lang } = useLang();
  const copy = copies[lang] || copies.en;

  const mailHref = `mailto:arsenio@centeo.one?subject=${encodeURIComponent(
    copy.mailSubject
  )}&body=${encodeURIComponent(copy.mailBody)}`;
  const panelClass =
    'rounded-3xl border border-slate-200/70 bg-white/95 p-5 shadow-sm backdrop-blur-sm sm:rounded-2xl sm:bg-white sm:p-8 lg:p-10';
  const cardClass = 'rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm sm:rounded-xl sm:p-6';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <section className="relative isolate min-h-[460px] overflow-hidden sm:h-[70vh] sm:min-h-[520px]">
        <div className="absolute inset-0">
          <div
            className="h-full w-full bg-cover bg-center"
            style={{ backgroundImage: "url('/puglia.jpg')" }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-slate-900/65" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" aria-hidden="true" />
        </div>
        <div className="relative z-10 flex h-full flex-col">
          <header className="mx-auto flex w-full max-w-6xl flex-col items-start gap-2 px-4 pt-5 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6">
            <span className="text-[0.7rem] font-semibold tracking-[0.3em] text-white/80 sm:text-xs">CENTEO</span>
            <span className="text-[0.68rem] font-medium uppercase tracking-[0.24em] text-white/70 sm:text-xs sm:tracking-wide">
              {copy.badge}
            </span>
          </header>
          <div className="mx-auto flex h-full w-full max-w-6xl flex-col justify-center px-4 pb-12 pt-16 sm:justify-end sm:px-6 sm:pb-20 sm:pt-0">
            <div className="max-w-xl text-white sm:max-w-2xl">
              <span className="inline-flex items-center gap-3 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white/80 sm:text-xs sm:tracking-[0.2em]">
                <span className="inline-block h-px w-8 bg-white/60" aria-hidden="true" />
                {copy.heroTag}
              </span>
              <h1 className="mt-6 text-[2rem] font-light leading-tight text-white sm:text-4xl sm:leading-snug md:text-5xl lg:text-[3.3rem]">
                {copy.headline}
              </h1>
              <p className="mt-5 text-[0.95rem] leading-relaxed text-white/85 sm:mt-6 sm:text-lg">
                {copy.paragraph}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
                <a
                  href={mailHref}
                  className="inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 sm:w-auto sm:px-8"
                >
                  {copy.ctaDeck}
                </a>
              </div>
              {copy.secondaryHeadline && (
                <div className="mt-8 rounded-2xl border border-white/20 bg-white/5 p-4 text-white/80 backdrop-blur-sm sm:mt-10 sm:rounded-none sm:border-0 sm:border-l sm:border-white/30 sm:bg-transparent sm:p-0 sm:pl-6 sm:backdrop-blur-none">
                  <div className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-xs">
                    {copy.secondaryHeadline}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-white/85 sm:text-base">
                    {copy.secondarySubheadline}
                  </p>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      <main className="mx-auto max-w-5xl space-y-10 px-4 py-12 sm:space-y-14 sm:px-6 sm:py-16 lg:space-y-16 lg:px-8">
        <section className={panelClass}>
          <div className="flex flex-col gap-6 sm:gap-8 lg:flex-row">
            <div className="lg:w-1/3">
              <h2 className="text-2xl font-semibold text-slate-900">{copy.valueTitle}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{copy.valueSubtitle}</p>
            </div>
            <div className="grid flex-1 gap-4 sm:gap-6 sm:grid-cols-2">
              {copy.tiles.map((tile) => (
                <div key={tile.title} className={cardClass}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {tile.title}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{tile.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-slate-900">{copy.quickFactsTitle}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{copy.quickFactsSubtitle}</p>
          </div>
          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {copy.quickFacts.map((fact) => (
              <div key={fact.label} className={cardClass}>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {fact.label}
                </div>
                <div className="mt-2 text-xl font-semibold text-slate-900">{fact.value}</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{fact.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={panelClass}>
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">{copy.structureTitle}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{copy.structureSubtitle}</p>
          </div>
          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2">
            {copy.structureItems.map((item) => (
              <div key={item.title} className={cardClass}>
                <div className="text-base font-semibold text-slate-900">{item.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={panelClass}>
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">{copy.diligenceTitle}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{copy.diligenceSubtitle}</p>
          </div>
          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2">
            {copy.diligenceItems.map((item) => (
              <div key={item.title} className={cardClass}>
                <div className="text-base font-semibold text-slate-900">{item.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-6 sm:gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className={panelClass}>
            <h2 className="text-2xl font-semibold text-slate-900">{copy.contactTitle}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{copy.contactSubtitle}</p>
            <p className="mt-6 text-sm text-slate-500">{copy.contactNote}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={mailHref}
                className="inline-flex w-full items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 sm:w-auto"
              >
                {copy.ctaContact}
              </a>
              <a
                href="tel:+493024618457"
                className="inline-flex w-full items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:border-slate-400 sm:w-auto"
              >
                +49 (0)30 24618457
              </a>
            </div>
          </div>
          <div className={panelClass}>
            <div className="space-y-5 text-sm sm:space-y-6">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {copy.contactEmailLabel}
                </div>
                <div className="mt-2 text-lg font-semibold text-slate-900">arsenio@centeo.one</div>
                <p className="mt-1 text-slate-600">Founder &amp; CEO</p>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {copy.contactPhoneLabel}
                </div>
                <div className="mt-2 text-lg font-semibold text-slate-900">+49 (0)30 24618457</div>
                <p className="mt-1 text-slate-600">Investor Relations | Lazzaro One UG (haftungsbeschränkt)</p>
              </div>
              <div className={`${cardClass} text-slate-600`}>
                <p>{copy.platformBlurb}</p>
              </div>
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <h2 className="text-2xl font-semibold text-slate-900">{copy.disclaimerTitle}</h2>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-600">
            {copy.disclaimerParagraphs.map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
        </section>
      </main>

      <FooterRedesign onShowAboutUs={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
    </div>
  );
};

export default InvestorLanding;
